import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import {
    IChecklist2ReportRecentActivity,
    IChecklistV2ReportParameter,
    IChecklistV2ReportParameterFilter,
} from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import {
    Checkpoint2MeasureUnitType,
    Checkpoint2StatusType,
    CheckpointAnswerLogType,
    CheckpointType,
} from '@fi-shared/checklist/checklist-enum';
import { AppTextService } from '@fi-shared/app-text';
import { FiSharedActionsService } from '@fi-app/utilities/fi-services/fi-shared-actions.service';
import { FiImageSize, FileType, ObjectTypes } from '@fi-shared/types';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiInfiniteScrollDataSource } from '@fi-shared/fi-items-list/fi-infinite-scroll-data-source';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { FiChecklist2ReportData } from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report-data';
import { FiChecklist2ReportHelperService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-helper.service';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { isDefined } from '@fi-shared/utils';
import { FiCheckpoint2HelperService } from '@fi-shared/checklist/fi-checkpoint2-helper.service';
import { FiChecklist2ReportDataService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-data.service';
import { FiChecklist2ReportRecentActivityService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-recent-activity.service';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';

@Component({
    selector: 'fi-recent-activity-widget-content',
    templateUrl: './recent-activity-widget-content.component.html',
    styleUrls: ['./recent-activity-widget-content.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-recent-activity-widget-content' },
    standalone: false,
})
export class FiChecklist2ReportRecentActivityWidgetContentComponent
    extends FiInfiniteScrollDataSource<IChecklist2ReportRecentActivity>
    implements OnInit, OnDestroy
{
    @Input({ required: true }) reportData: FiChecklist2ReportData;
    @Input({ required: true }) reportParameter: IChecklistV2ReportParameter;
    @Input({ required: true }) currentFilter: IChecklistV2ReportParameterFilter;
    @Input() isWidgetMode: boolean;
    @Input() isWideLayout: boolean;
    @Output() onOpenChecklist = new EventEmitter<void>();
    objectType: ObjectTypes;
    itemsListType: FiItemsListType;
    protected filteredRecentActivity: IChecklist2ReportRecentActivity[] = [];

    @Input('filteredRecentActivity') set filteredRecentActivityUpdate(value: IChecklist2ReportRecentActivity[]) {
        if (isDefined(value)) this.setNewRecentActivityData(value);
    }

    get isAuditMode() {
        return this.reportData.auditMode;
    }

    constructor(
        private appTextService: AppTextService,
        private sharedActionsService: FiSharedActionsService,
        private fiStateService: FiStateService,
        private fiChecklist2ReportRecentActivityService: FiChecklist2ReportRecentActivityService,
        private fiChecklist2ReportHelperService: FiChecklist2ReportHelperService,
        private fiChecklist2ReportDataService: FiChecklist2ReportDataService,
        private mediaStorageService: FiMediaStorageService,
        private fiCheckpoint2HelperService: FiCheckpoint2HelperService,
        private fiSignalRService: FiSignalRService,
        injector: Injector,
    ) {
        super(injector);
    }

    ngOnInit() {
        this.objectType = this.isAuditMode ? ObjectTypes.Checklist2AuditReport : ObjectTypes.Checklist2Report;
        this.itemsListType = this.isAuditMode
            ? FiItemsListType.ChecklistV2AuditRecentActivity
            : FiItemsListType.ChecklistV2RecentActivity;

        if (!this.filteredRecentActivity?.length) {
            this.setNewRecentActivityData(this.reportData.getRecentActivity(this.reportParameter.Id));
        }

        // todo move these parts to parent components?
        if (this.isWidgetMode) {
            // reload recent activity data on checklist2ViewerChangedEvent SignalR
            this.subscribeOnChecklistAnswersChange();
        }

        // don't reload data in recent activity widget
        if (!this.isWidgetMode) {
            this.fiChecklist2ReportDataService.subscribeOnFilterOrDateRangeChanged(
                this.reportParameter.Id,
                this.ngOnDestroy$,
                () => {
                    this.loadRecentActivityData();
                },
            );
        }
    }

    private loadRecentActivityData(): Promise<IChecklist2ReportRecentActivity[]> {
        const promise = this.fiChecklist2ReportRecentActivityService.getChecklistRecentActivity(
            this.currentFilter,
            this.fiChecklist2ReportDataService.getRecentHistoryDateRange(),
            this.reportData.auditMode,
        );
        promise.then((data) => {
            this.setNewRecentActivityData(data);
        });
        return promise;
    }

    setNewRecentActivityData(recentActivity: IChecklist2ReportRecentActivity[]) {
        this.filteredRecentActivity = recentActivity;
        this.reloadData();
    }

    private updateRecentActivityTimer: NodeJS.Timeout;
    private updateRecentActivityTimeout: number = 3000;
    private subscribeOnChecklistAnswersChange() {
        this.fiSignalRService.checklist2ViewerChangedEvent.subscribe(() => {
            if (this.updateRecentActivityTimer) {
                clearTimeout(this.updateRecentActivityTimer);
                this.updateRecentActivityTimer = undefined;
            }

            this.updateRecentActivityTimer = setTimeout(() => {
                // reload recent activity data on checklist2ViewerChangedEvent SignalR amd update recent activities in reportData
                this.loadRecentActivityData().then((data) => {
                    this.reportData.setRecentActivity(data, this.reportParameter.Id);
                    this.cdr.markForCheck();
                });
            }, this.updateRecentActivityTimeout);
        }, this.ngOnDestroy$);
    }

    getListData(): Promise<IChecklist2ReportRecentActivity[]> {
        const promise = Promise.resolve(this.filteredRecentActivity);
        promise.then(() => this.cdr.markForCheck());
        return promise;
    }

    openUserProfile(userId: number) {
        this.sharedActionsService.openUserViewPopup(userId);
    }

    getActivityActionLabel(reportActivityItem: IChecklist2ReportRecentActivity): string {
        if (reportActivityItem.AnswerLogType === CheckpointAnswerLogType.Commenting) {
            return this.appTextService.get('checklist.report_recent_activity_comment_added', {
                '{0}': reportActivityItem.CheckpointName,
                '{1}': reportActivityItem.Comments[0].Content,
            });
        }

        if (reportActivityItem.AnswerLogType === CheckpointAnswerLogType.UpdatingComment) {
            return this.appTextService.get('checklist.report_recent_activity_comment_updated', {
                '{0}': reportActivityItem.CheckpointName,
                '{1}': reportActivityItem.Comments[0].Content,
            });
        }

        if (reportActivityItem.AnswerLogType === CheckpointAnswerLogType.RemovingComment) {
            return this.appTextService.get('checklist.report_recent_activity_comment_removed', {
                '{0}': reportActivityItem.CheckpointName,
                '{1}': reportActivityItem.Comments[0].Content,
            });
        }

        if (this.reportingAnswerLogTypes.includes(reportActivityItem.AnswerLogType)) {
            return reportActivityItem.CheckpointType === CheckpointType.Status
                ? this.appTextService.get('checklist.report_recent_activity_answer_added', {
                      '{0}': reportActivityItem.CheckpointName,
                  })
                : this.appTextService.get('checklist.report_recent_activity_answer_added_measurement', {
                      '{0}': reportActivityItem.CheckpointName,
                      '{1}': reportActivityItem.AnswerValue,
                      '{2}': this.getMeasureUnitsText(reportActivityItem.CheckpointMeasureUnitType),
                  });
        }

        if (reportActivityItem.AnswerLogType === CheckpointAnswerLogType.ChangingReportedValue) {
            return reportActivityItem.CheckpointType === CheckpointType.Status
                ? this.appTextService.get('checklist.report_recent_activity_answer_updated', {
                      '{0}': reportActivityItem.CheckpointName,
                  })
                : this.appTextService.get('checklist.report_recent_activity_answer_updated_measurement', {
                      '{0}': reportActivityItem.CheckpointName,
                      '{1}': reportActivityItem.AnswerValue,
                      '{2}': this.getMeasureUnitsText(reportActivityItem.CheckpointMeasureUnitType),
                  });
        }

        if (reportActivityItem.AnswerLogType === CheckpointAnswerLogType.AddingAttachment) {
            return this.appTextService.get('checklist.report_recent_activity_attachment_added', {
                '{0}': reportActivityItem.CheckpointName,
            });
        }

        if (reportActivityItem.AnswerLogType === CheckpointAnswerLogType.RemovingAttachment) {
            return this.appTextService.get('checklist.report_recent_activity_attachment_removed', {
                '{0}': reportActivityItem.CheckpointName,
            });
        }

        if (reportActivityItem.AnswerLogType === CheckpointAnswerLogType.ClearedCheckpoint) {
            return this.appTextService.get('checklist.report_recent_activity_checkpoint_cleared', {
                '{0}': reportActivityItem.CheckpointName,
            });
        }

        // todo we no CheckpointAnswerLogType.UpdatingAttachment
        /*if (reportActivityItem.AnswerLogType === CheckpointAnswerLogType.UpdatingAttachment) {
            return this.appTextService.get('checklist.report_recent_activity_attachment_updated', {
                '{0}': reportActivityItem.CheckpointName
            });
        }*/
    }

    getMeasureUnitsText(measureUnitType: Checkpoint2MeasureUnitType): string {
        return this.fiCheckpoint2HelperService.getCheckpointMeasureUnitShortText(measureUnitType);
    }

    getCheckpointStatusIconClassName(checkpointStatusType: Checkpoint2StatusType): string {
        if (!isDefined(checkpointStatusType)) {
            return this.checkpointClearedStatusIconClassName;
        }
        if (!this.isCheckpointDeviationStatus(checkpointStatusType)) return this.checkpointOkStatusIconClassName;
        if (this.isCheckpointDeviationStatus(checkpointStatusType)) return this.checkpointNotOkStatusIconClassName;
    }

    getCheckpointStatusLabel(checkpointStatusType: Checkpoint2StatusType): string {
        return this.fiChecklist2ReportHelperService.getAnswerStatusLabel(checkpointStatusType);
    }

    openChecklist(checklistToScheduleId: number, businessUnitId: number, checklistPeriodId: number) {
        const viewState: string = FiStateNames.getChecklistV2RootState(this.isAuditMode).View;
        this.fiStateService.registerReturnToCurrentState(viewState);
        this.fiStateService.go(viewState, {
            id: checklistToScheduleId,
            isPreviewMode: true,
            previewUnitId: businessUnitId,
            businessUnitId,
            previewPeriodId: checklistPeriodId,
        });
        this.onOpenChecklist.emit();
    }

    getCheckpointStatusClassName(checkpointStatusType: Checkpoint2StatusType): { [key: string]: boolean } {
        return {
            [this.checkpointStatusClassNameDeviation]:
                isDefined(checkpointStatusType) && this.isCheckpointDeviationStatus(checkpointStatusType),
            [this.checkpointStatusClassNameAnswered]:
                isDefined(checkpointStatusType) && !this.isCheckpointDeviationStatus(checkpointStatusType),
        };
    }

    private isCheckpointDeviationStatus(checkpointStatusType: Checkpoint2StatusType): boolean {
        return checkpointStatusType === Checkpoint2StatusType.NotOk;
    }

    isRegularVideoAttachment(mediaBankFileReference: IMediaBankFileReference): boolean {
        return (
            mediaBankFileReference.FileType === FileType.Movie &&
            !this.mediaStorageService.isVimeoStorage(mediaBankFileReference.StorageType)
        );
    }

    protected readonly FiProfileSizes = FiProfileSizes;
    private readonly checkpointOkStatusIconClassName: string = 'fas fa-circle-check';
    private readonly checkpointNotOkStatusIconClassName: string = 'fas fa-circle-exclamation';
    private readonly checkpointClearedStatusIconClassName: string = 'fa-solid fa-eraser';

    private readonly checkpointStatusClassNameAnswered: string = 'answered';
    private readonly checkpointStatusClassNameDeviation: string = 'deviation';

    private readonly reportingAnswerLogTypes: CheckpointAnswerLogType[] = [
        CheckpointAnswerLogType.ReportingAsOk,
        CheckpointAnswerLogType.ReportingAsNotOk,
    ];
    protected readonly FiImageSize = FiImageSize;
    protected readonly isDefined = isDefined;
}
